import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  useTheme,
  useMediaQuery,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  Folder,
  ExitToApp,
  Person,
  Assignment,
  SendToMobile,
  CalendarToday,
  ContactPhone,
  Link as LinkIcon,
  BeachAccess as PTOIcon,
  Newspaper,
  CheckCircle as ApprovalsIcon,
  LocalShipping,
  PersonSearch as CandidatesIcon,
  PersonAdd as NewHiresIcon,
  Place as PlaceIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout, isManager } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/', show: true },
    { text: 'News', icon: <Newspaper />, path: '/news', show: true },
    { text: 'Vehicle Inspections', icon: <LocalShipping />, path: '/vehicle-docs', show: true },
    { text: 'Calendar', icon: <CalendarToday />, path: '/calendar', show: true },
    // { text: 'My PTO', icon: <PTOIcon />, path: '/pto/my-pto', show: true },
    // { text: 'PTO Approvals', icon: <ApprovalsIcon />, path: '/pto/approvals', show: isManager },
    { text: 'Candidates', icon: <CandidatesIcon />, path: '/candidates', show: true },
    { text: 'New Hires', icon: <NewHiresIcon />, path: '/new-hires', show: true },
    { text: 'HR Documents', icon: <Folder />, path: '/documents', show: true },
    { text: 'Employee Directory', icon: <ContactPhone />, path: '/directory', show: true },
    { text: 'Useful Links', icon: <LinkIcon />, path: '/links', show: true },
    { text: 'Send Document', icon: <SendToMobile />, path: '/send-document', show: true },
    { text: 'Request Form', icon: <Assignment />, path: '/request-form', show: true },
    { text: 'Users', icon: <People />, path: '/users', show: isManager },
    { text: 'Locations', icon: <PlaceIcon />, path: '/locations', show: isManager },
    { text: 'Profile', icon: <Person />, path: '/profile', show: true },
  ];

  const drawer = (
    <Box>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          MWAT Internal
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems
          .filter(item => item.show)
          .map((item) => (
            <ListItem
              button
              key={item.text}
              onClick={() => {
                navigate(item.path);
                if (isMobile) {
                  setMobileOpen(false);
                }
              }}
              selected={location.pathname === item.path}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItem>
          ))}
      </List>
      <Divider />
      <List>
        <ListItem button onClick={handleLogout}>
          <ListItemIcon>
            <ExitToApp />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItem>
      </List>
    </Box>
  );

  const drawerWidth = 240;

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Midwest Automotive Trucking
          </Typography>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {user?.first_name} {user?.last_name} ({user?.role})
          </Typography>
          <Button color="inherit" onClick={handleLogout}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;